/**
 * A-02 «Способ входа» и A-03 «Ввод кода».
 *
 * Оба шага живут в одном экране: между ними сохраняется введённый контакт,
 * а «Сменить способ входа» возвращает на шаг 1 без потери данных. Черновик
 * контакта живёт в сторе приложения — уход в A-07 «Не могу войти» и возврат
 * не заставляют набирать номер заново.
 *
 * Телефон вводится через маску `+7 (900) 123-45-67` (`PhoneInput`): 8 900…,
 * +7 900…, 7900… и 900… — один и тот же номер, на сервер уходит E.164.
 * Ошибки называют причину: сколько цифр не хватает, почему адрес не подходит,
 * когда можно повторить запрос.
 *
 * ПС §06 экран 3; BS §5.2; коды ошибок — `useSignIn`.
 */
import { Link, useNavigate, useRouter } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSignIn } from '@features/auth/use-sign-in'
import { MOCKS_ENABLED } from '@shared/api/transport'
import { useHasPointerFine } from '@shared/lib/use-media-query'
import type { IdentityKind } from '@shared/api/types'
import {
  EMPTY_PHONE,
  emailProblem,
  formatPhone,
  parsePhoneInput,
  phoneProblem,
  toE164,
  type PhoneValue,
} from '@shared/lib/phone'
import { Button, CodeInput, Field, Icon, Input, PhoneInput, SegmentedControl, cx } from '@shared/ui'

import { publishSessionStartState, startSession, type SessionStartState } from '../../boot/bootstrap'
import { type AuthTransitionGuard, withAuthTransition } from '../../boot/auth-transition'
import { recoverPendingServerLogout } from '../../boot/session-lifecycle'
import { ScreenFrame, type ScreenFrameHandle } from '../../layouts/ScreenFrame'
import { isBannedAppealSession } from '../../model/account-access'
import { useAppStore } from '../../model/app-store'
import { getPreviousPath } from '../../model/navigation-direction'
import styles from './auth.module.css'

export function SignInScreen(): React.ReactElement {
  const navigate = useNavigate()
  const router = useRouter()
  const frameRef = useRef<ScreenFrameHandle>(null)
  const draft = useAppStore((s) => s.signInDraft)
  const setSignInDraft = useAppStore((s) => s.setSignInDraft)

  // Черновик восстанавливается один раз при открытии экрана (возврат из A-07).
  const [kind, setKind] = useState<IdentityKind>(draft?.kind ?? 'phone')
  const [phone, setPhone] = useState<PhoneValue>(() =>
    draft?.kind === 'phone' && draft.identifier ? parsePhoneInput(draft.identifier) : EMPTY_PHONE,
  )
  const [email, setEmail] = useState(draft?.kind === 'email' ? draft.identifier : '')
  const [code, setCode] = useState('')
  /** Локальная проверка показывается после первой попытки отправить, а не на каждом символе. */
  const [attempted, setAttempted] = useState(false)
  /** Verify уже ответил, но `/me` и запуск сессии ещё не завершились. */
  const [finalizing, setFinalizing] = useState(false)
  const [transitionError, setTransitionError] = useState<string | null>(null)
  /*
   * State обновится лишь в следующем render. Ref закрывает два submit в одном
   * такте и окно между успешным verify и переходом с экрана.
   */
  const codeSubmissionRef = useRef(false)

  const signIn = useSignIn()
  const step: 'identifier' | 'code' = signIn.challenge ? 'code' : 'identifier'
  const codePending = signIn.pending || finalizing
  // Шаг контакта анимируется «назад» только после возврата со второго шага.
  const visitedCode = useRef(false)
  if (step === 'code') visitedCode.current = true

  /** К стартовому экрану: историей, если пришли с него, иначе явным переходом. */
  const leave = (): void => {
    if (getPreviousPath() === '/') router.history.back()
    else void navigate({ to: '/' })
  }

  const goBack = (): void => {
    if (codeSubmissionRef.current) return
    if (step === 'code') {
      signIn.reset()
      setCode('')
      setTransitionError(null)
      return
    }
    if (frameRef.current && getPreviousPath() === '/') frameRef.current.pop(leave)
    else leave()
  }

  // Значение для сервера и понятная причина, почему его ещё нет.
  const identifier =
    kind === 'phone' ? toE164(phone) : emailProblem(email) ? null : email.trim().toLowerCase()
  const localProblem = kind === 'phone' ? phoneProblem(phone) : emailProblem(email)
  // Кнопка активна, как только что-то введено: нажатие показывает, чего именно не хватает.
  const hasInput = kind === 'phone' ? phone.digits.length > 0 : email.trim().length > 0

  useEffect(() => {
    setSignInDraft({ kind, identifier: kind === 'phone' ? formatPhone(phone) : email })
  }, [kind, phone, email, setSignInDraft])

  /* --- Шаг 1 --------------------------------------------------------- */

  const submitIdentifier = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setAttempted(true)
    if (!identifier || localProblem || signIn.pending) return
    await signIn.requestCode(kind, identifier)
  }

  /* --- Шаг 2 --------------------------------------------------------- */

  const submitCode = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (code.length < 6 || codePending || codeSubmissionRef.current) return

    codeSubmissionRef.current = true
    setFinalizing(true)
    setTransitionError(null)
    let completed = false

    try {
      const outcome = await withAuthTransition(
        async (ensureOwned): Promise<{ destination: string; session: SessionStartState } | null> => {
          // Offline logout мог оставить cookie и durable marker. Его нужно
          // довести до verify, пока cookie ещё относится к старой epoch.
          // Иначе marker U на следующем bootstrap отзовёт уже свежую
          // same-user OTP-сессию U.
          if (!(await recoverPendingServerLogout(ensureOwned))) {
            setTransitionError('Не удалось завершить предыдущий выход. Проверьте сеть и попробуйте снова.')
            return null
          }

          const result = await signIn.verify(code, true, ensureOwned)
          if (!result) {
            setCode('')
            return null
          }

          // Новый аккаунт → onboarding (A-05); иначе последний раздел (SYS-17).
          const started = await fetchMeAndStart(ensureOwned)
          if (!started) return null
          const { me, session } = started
          setSignInDraft(null)

          if (result.is_new || me.user.status === 'onboarding') {
            return { destination: '/onboarding', session }
          }
          if (isBannedAppealSession(me)) {
            return { destination: '/settings/restrictions', session }
          }

          const pending = useAppStore.getState().pendingPath
          useAppStore.getState().setPendingPath(null)
          return { destination: pending ?? '/dvizh', session }
        },
      )

      if (!outcome) return
      // Safari может перевести документ в pagehide/bfcache сразу после смены
      // маршрута. Сначала полностью освобождаем origin-wide auth lock, и лишь
      // затем показываем интерактивный экран новой сессии.
      publishSessionStartState(outcome.session)
      await navigate({ to: outcome.destination })
      completed = true
    } catch {
      setTransitionError('Не удалось завершить вход. Проверьте сеть и попробуйте снова.')
    } finally {
      // На успехе форма уже уходит с экрана; снимать затвор до размонтирования нельзя.
      if (!completed) {
        codeSubmissionRef.current = false
        setFinalizing(false)
      }
    }
  }

  async function fetchMeAndStart(ensureOwned: AuthTransitionGuard) {
    const { api, authTransitionRequest, unwrap } = await import('@shared/api/client')
    try {
      await ensureOwned()
      const me = unwrap(await api.GET('/me', authTransitionRequest()))
      await ensureOwned()
      const session = await startSession(me, ensureOwned, { publishInteractiveState: false })
      return { me, session }
    } catch {
      return null
    }
  }

  return (
    <ScreenFrame
      ref={frameRef}
      nested
      onBack={step === 'identifier' ? leave : undefined}
      className={styles['page']}
    >
      <div className={styles['topBar']}>
        <button
          type="button"
          className={styles['circleBack']}
          aria-label="Назад"
          onClick={goBack}
          disabled={codePending}
        >
          <Icon name="chevron-left" size={24} />
        </button>
      </div>

      <main className={cx(styles['main'], styles['mainCentered'], styles['mainTop'], styles['mainBelowBar'])}>
        <div className={styles['card']}>
          {/* key — смена шага проигрывает сдвиг: вперёд справа, назад слева, как экраны */}
          <div
            key={step}
            className={cx(
              styles['step'],
              step === 'code' && styles['stepForward'],
              step === 'identifier' && visitedCode.current && styles['stepBack'],
            )}
          >
            {step === 'identifier' ? (
              <IdentifierStep
                kind={kind}
                onKindChange={(next) => {
                  setKind(next)
                  setAttempted(false)
                }}
                phone={phone}
                onPhoneChange={setPhone}
                email={email}
                onEmailChange={setEmail}
                error={signIn.error ?? (attempted ? localProblem : null)}
                pending={signIn.pending}
                ready={hasInput}
                onSubmit={submitIdentifier}
              />
            ) : (
              <CodeStep
                kind={kind}
                identifierLabel={kind === 'phone' ? formatPhone(phone) : email.trim()}
                code={code}
                onCodeChange={(next) => {
                  setCode(next)
                  setTransitionError(null)
                }}
                error={transitionError ?? signIn.error}
                pending={codePending}
                resendAfter={signIn.challenge?.resend_after ?? null}
                delivery={signIn.challenge?.delivery ?? null}
                fallbackDelivery={signIn.challenge?.fallback_delivery ?? null}
                onSwitchDelivery={() => {
                  if (identifier) void signIn.switchDelivery(kind, identifier)
                }}
                onResend={() => {
                  if (identifier) void signIn.resend(kind, identifier)
                }}
                onChangeMethod={() => {
                  signIn.reset()
                  setCode('')
                }}
                onSubmit={submitCode}
              />
            )}
          </div>
        </div>
      </main>
    </ScreenFrame>
  )
}

/* ===================================================================== */
/* Шаг 1 — контакт                                                        */
/* ===================================================================== */

interface IdentifierStepProps {
  kind: IdentityKind
  onKindChange: (kind: IdentityKind) => void
  phone: PhoneValue
  onPhoneChange: (value: PhoneValue) => void
  email: string
  onEmailChange: (value: string) => void
  error: string | null
  pending: boolean
  ready: boolean
  onSubmit: (event: React.FormEvent) => void
}

function IdentifierStep({
  kind,
  onKindChange,
  phone,
  onPhoneChange,
  email,
  onEmailChange,
  error,
  pending,
  ready,
  onSubmit,
}: IdentifierStepProps) {
  /*
   * Автофокус поля — только с точным указателем. На телефоне он поднимал клавиатуру
   * прямо во время въезда экрана: Safari прокручивал документ к полю, которое ещё за
   * правым краем, раскладка сжималась под клавиатуру поверх анимации, а отложенный
   * focus() на iOS клавиатуру уже не показывает — пусть поле откроет сам человек.
   */
  const autoFocusFields = useHasPointerFine()
  return (
    <form onSubmit={onSubmit} noValidate className={styles['step']}>
      {/* Композиция как у входа в Telegram: иллюстрация шага, заголовок и пояснение по центру */}
      <div className={styles['hero']}>
        <div key={kind} className={styles['heroIcon']} aria-hidden>
          <Icon name={kind === 'phone' ? 'phone' : 'mail'} size={40} />
        </div>
        <div className={styles['heroText']}>
          <h1 className={styles['heading']}>{kind === 'phone' ? 'Ваш телефон' : 'Ваша почта'}</h1>
          <p className={styles['lead']}>
            {kind === 'phone'
              ? 'Отправим код в «Движ» на ваших устройствах или в SMS — пароль не нужен.'
              : 'Отправим письмо с кодом — пароль придумывать не нужно.'}
          </p>
        </div>
      </div>

      <div className={styles['kindSwitch']}>
        <SegmentedControl
          value={kind}
          onValueChange={onKindChange}
          fullWidth
          aria-label="Способ входа"
          segments={[
            { value: 'phone', label: 'Телефон', icon: 'phone' },
            { value: 'email', label: 'Почта', icon: 'mail' },
          ]}
        />
      </div>

      <div className={styles['fieldBlock']}>
        {kind === 'phone' ? (
          <Field
            label="Номер телефона"
            error={error}
            hint={
              phone.mode === 'intl'
                ? 'Код страны и номер без пробелов'
                : 'Для другого кода страны начните ввод с «+»'
            }
          >
            {({ id, describedBy, invalid }) => (
              <PhoneInput
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={phone}
                onValueChange={onPhoneChange}
                placeholder="+7 (900) 123-45-67"
                autoFocus={autoFocusFields}
              />
            )}
          </Field>
        ) : (
          <Field label="Адрес почты" error={error} hint="Пришлём письмо с кодом подтверждения">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="name@example.com"
                autoFocus={autoFocusFields}
              />
            )}
          </Field>
        )}
      </div>

      <div className={styles['actions']}>
        <Button type="submit" variant="primary" size="lg" fullWidth loading={pending} disabled={!ready}>
          Получить код
        </Button>

        <div className={styles['links']}>
          <Link to="/support/cant-login" className={styles['linkButton']}>
            Не могу войти
          </Link>
        </div>
      </div>
    </form>
  )
}

/* ===================================================================== */
/* Шаг 2 — код                                                            */
/* ===================================================================== */

interface CodeStepProps {
  kind: IdentityKind
  identifierLabel: string
  code: string
  onCodeChange: (value: string) => void
  error: string | null
  pending: boolean
  resendAfter: string | null
  /** Изменение v1.12: куда ушёл код — `app`, `sms` или `email`. */
  delivery: 'app' | 'sms' | 'email' | null
  /** Канал, на который можно переключиться кнопкой «код не пришёл»; null — некуда. */
  fallbackDelivery: 'sms' | 'email' | null
  onSwitchDelivery: () => void
  onResend: () => void
  onChangeMethod: () => void
  onSubmit: (event: React.FormEvent) => void
}

function CodeStep({
  kind,
  identifierLabel,
  code,
  onCodeChange,
  error,
  pending,
  resendAfter,
  delivery,
  fallbackDelivery,
  onSwitchDelivery,
  onResend,
  onChangeMethod,
  onSubmit,
}: CodeStepProps) {
  const secondsLeft = useCountdown(resendAfter)
  const formRef = useRef<HTMLFormElement>(null)
  const submittedCode = useRef<string | null>(null)

  /*
   * Код введён полностью — отправляем без лишнего нажатия, но ровно один раз:
   * когда проверка завершается, `pending` снова false при тех же шести цифрах,
   * и без метки повторная отправка уходила на уже использованный challenge (410).
   */
  useEffect(() => {
    if (code.length < 6) {
      submittedCode.current = null
      return
    }
    if (!pending && submittedCode.current !== code) {
      submittedCode.current = code
      formRef.current?.requestSubmit()
    }
  }, [code, pending])

  return (
    <form ref={formRef} onSubmit={onSubmit} noValidate className={styles['step']}>
      <div className={styles['hero']}>
        <div className={styles['heroIcon']} aria-hidden>
          <Icon name={delivery === 'app' ? 'shield' : 'chats'} size={40} />
        </div>
        <div className={styles['heroText']}>
          <h1 className={styles['heading']}>Введите код</h1>
          {/*
           * Изменение v1.12. Если вход в «Движ» уже где-то выполнен, код приходит туда —
           * как в Telegram. Тогда важно сказать, куда именно смотреть, иначе человек будет
           * ждать SMS, которой не будет.
           */}
          {delivery === 'app' ? (
            <p className={styles['lead']}>
              Отправили код в «Движ» на ваших устройствах — откройте уведомления там
            </p>
          ) : (
            <p className={styles['lead']}>
              {kind === 'phone' ? 'Отправили SMS на ' : 'Отправили письмо на '}
              <span className={styles['identifier']}>{identifierLabel}</span>
              {kind === 'email' ? ' — проверьте почту и папку «Спам»' : ''}
            </p>
          )}
        </div>
      </div>

      <div className={styles['codeBlock']}>
        <Field error={error}>
          {({ id, describedBy, invalid }) => (
            <CodeInput
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={code}
              onValueChange={onCodeChange}
              disabled={pending}
              autoFocus
            />
          )}
        </Field>

        {MOCKS_ENABLED ? (
          <p className={styles['devHint']}>
            <Icon name="info" size={15} />
            Демо-режим: код <strong>000000</strong>
          </p>
        ) : null}
      </div>

      <div className={styles['actions']}>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={pending}
          disabled={code.length < 6}
        >
          Войти
        </Button>

        <div className={styles['links']}>
          {/* Смена канала не ждёт таймера: сервер разрешает её сразу (BS §5.2, v1.12). */}
          {fallbackDelivery ? (
            <button
              type="button"
              className={styles['linkButton']}
              onClick={onSwitchDelivery}
              disabled={pending}
            >
              {fallbackDelivery === 'sms' ? 'Отправить код в SMS' : 'Отправить код на почту'}
            </button>
          ) : null}

          {secondsLeft > 0 ? (
            <span>Отправить новый код можно через {secondsLeft} с</span>
          ) : (
            <button type="button" className={styles['linkButton']} onClick={onResend} disabled={pending}>
              Отправить код ещё раз
            </button>
          )}

          <button type="button" className={styles['linkButton']} onClick={onChangeMethod} disabled={pending}>
            Изменить {kind === 'phone' ? 'номер' : 'адрес'} или способ входа
          </button>
        </div>
      </div>
    </form>
  )
}

/**
 * Обратный отсчёт до `resend_after`.
 * BS §17.2 / правка CH-02: `resend_after` — МОМЕНТ времени (RFC 3339),
 * а не длительность. Вычитаем из него текущее время.
 */
function useCountdown(until: string | null): number {
  const target = useMemo(() => (until ? Date.parse(until) : 0), [until])
  const [left, setLeft] = useState(() => remaining(target))

  useEffect(() => {
    if (!target) return
    setLeft(remaining(target))

    const timer = setInterval(() => {
      const next = remaining(target)
      setLeft(next)
      if (next <= 0) clearInterval(timer)
    }, 1000)

    return () => clearInterval(timer)
  }, [target])

  return left
}

function remaining(target: number): number {
  if (!target) return 0
  return Math.max(0, Math.ceil((target - Date.now()) / 1000))
}
